<script setup lang="ts">
import { ButtonV2, HugeIcon } from '@teachino/shared-design-system';
import { readFragment, type FragmentOf } from '@teachino/shared-infrastructure';
import { $t, type Translation } from '@teachino/shared-i18n';
import { useDateFormat } from '@teachino/shared-util';
import { computed, ref } from 'vue';
import { personalLicenseFragment } from './personal-license.fragment';
import { getPersonalPaywallGraceDayCounts } from './paywall-grace-day-count';
import { useLicense } from './useLicense';

const { formatDayLongWithYear } = useDateFormat();
const {
  startManageSubscription,
  highlightsForFeatures,
  paywallLicenses,
  trialDayCounts,
} = useLicense();

const FEATURE_ICON_SIZE = 16;

const props = defineProps<{
  license: FragmentOf<typeof personalLicenseFragment>;
}>();

const license = computed(() =>
  readFragment(personalLicenseFragment, props.license),
);

const isManageSubscriptionLoading = ref(false);

const manageSubscription = async (licenseId: string): Promise<void> => {
  isManageSubscriptionLoading.value = true;
  await startManageSubscription(licenseId);
  isManageSubscriptionLoading.value = false;
};

const licenseFeatureHighlights = computed<string[]>(() =>
  highlightsForFeatures(license.value.features),
);

const isFreePersonalLicense = computed(
  () =>
    license.value.plan.type === 'FREE' ||
    license.value.plan.key === 'teacher_free' ||
    license.value.plan.key === 'teacher_free_light',
);

const licenseTypeLabel = computed(() => {
  if (isFreePersonalLicense.value) {
    return $t('upgrade.license_page.free_license_label');
  }

  return `${$t('upgrade.license_page.upgrade.personal_licenses')} · ${$t(
    `upgrade.plan.type.${license.value.plan.type.toLowerCase()}` as Translation,
  )}`;
});

const graceDayCounts = computed(() =>
  getPersonalPaywallGraceDayCounts(paywallLicenses.value),
);

const remainingAccessDaysLabel = computed(() => {
  if (license.value.plan.type === 'TRIAL' && trialDayCounts.value) {
    return $t('navigation.profile.trial_days_left', {
      days: trialDayCounts.value.trialDaysLeft.toString(),
    });
  }

  if (!isFreePersonalLicense.value) {
    return null;
  }

  if (graceDayCounts.value) {
    return $t('navigation.profile.grace_days_left', {
      days: graceDayCounts.value.graceDaysLeft.toString(),
    });
  }

  if (trialDayCounts.value) {
    return $t('navigation.profile.trial_days_left', {
      days: trialDayCounts.value.trialDaysLeft.toString(),
    });
  }

  return null;
});
</script>

<template>
  <article class="rounded-lg border border-slate-100 bg-slate-50/70 p-4">
    <div class="mb-3 flex flex-wrap items-center gap-2">
      <span
        class="bg-success-50 text-success-700 rounded-md px-2 py-1 text-sm font-semibold"
      >
        {{ $t(`upgrade.plan.${license.plan.key}` as Translation) }}
      </span>
      <span class="text-sm text-slate-600">
        {{ licenseTypeLabel }}
      </span>
    </div>

    <dl class="space-y-2 text-sm text-slate-700">
      <div v-if="license.cycleStartAt">
        <dt class="inline">
          {{ $t('upgrade.license_page.license_started_at') }}
        </dt>
        <dd class="ms-1 inline font-medium text-slate-900">
          {{ formatDayLongWithYear(new Date(license.cycleStartAt)) }}
        </dd>
      </div>
      <div v-if="license.expiresAt">
        <dt class="inline">
          {{ $t('upgrade.license_page.license_expires_at') }}
        </dt>
        <dd class="ms-1 inline font-medium text-slate-900">
          {{ formatDayLongWithYear(new Date(license.expiresAt)) }}
        </dd>
      </div>
      <div v-else-if="license.cycleRenewalAt">
        <dt class="inline">
          {{ $t('upgrade.license_page.license_renews_at') }}
        </dt>
        <dd class="ms-1 inline font-medium text-slate-900">
          {{ formatDayLongWithYear(new Date(license.cycleRenewalAt)) }}
        </dd>
      </div>
      <div>
        <dt class="inline">{{ $t('upgrade.license_page.license_id') }}</dt>
        <dd class="ms-1 inline font-medium text-slate-900">{{ license.id }}</dd>
      </div>
      <div v-if="remainingAccessDaysLabel" class="text-slate-600">
        {{ remainingAccessDaysLabel }}
      </div>
    </dl>

    <ButtonV2
      v-if="license.cycleRenewalAt"
      class="mt-4"
      variant="primary"
      :label="$t('upgrade.license_page.manage')"
      :loading="isManageSubscriptionLoading"
      @click="void manageSubscription(license.id)"
    />

    <div class="mt-4 border-t border-slate-100 pt-4">
      <p class="mb-2 text-sm font-semibold text-slate-900">
        {{ $t('upgrade.license_page.features') }}
      </p>
      <ul class="space-y-1 text-sm text-slate-700">
        <li
          v-for="featureHighlight in licenseFeatureHighlights"
          :key="featureHighlight"
          class="flex items-start gap-2"
        >
          <span class="text-primary-600 mt-0.5 shrink-0">
            <HugeIcon icon="checkmark_circle" :size="FEATURE_ICON_SIZE" />
          </span>
          <span>{{ featureHighlight }}</span>
        </li>
      </ul>
    </div>
  </article>
</template>
